import { useRouter } from "next/router";
import { CreateOrderFn } from "~hooks";
import { useEffect } from "react";
import { CreateOrderInput, LineItemType, OrderType } from "~graphql/sdk";
import { useReferralCode } from "~hooks/useReferralCode";
import { useCreateOrder } from "~hooks/reservation/useCreateOrder";
import { IReleaseTicket, IReleaseZone } from "./nonseated-reservation.types";

export function useCreateGAOrder({
  releaseId,
  membershipId,
  eventId, // Allow passing event ID
}: {
  releaseId?: string;
  membershipId?: string;
  eventId?: string;
}) {
  const router = useRouter();
  const { referralIdOrCode } = useReferralCode();
  const { createOrderAndWaitForCompletion } = useCreateOrder();

  const handleGAOrderCreation: CreateOrderFn = async (
    items: IReleaseTicket[],
    addonItems: IReleaseTicket[],
    multiBuyId,
    errorCallback,
    onSuccess,
    options
  ) => {
    const type = !membershipId ? "event" : "membership";

    const orderType = Object.values(OrderType).includes(
      router.query.type as OrderType
    )
      ? (router.query.type as OrderType)
      : router.query.ownedTickets
      ? OrderType.ChangeSeats
      : undefined;

    const releasePasswordId =
      sessionStorage.getItem("releasePasswordId") ?? undefined;

    // V2 - currently only works for memberships
    const accessCodeId = sessionStorage.getItem("accessCodeId") ?? undefined;

    const ownedTickets = router.query?.ownedTickets;
    const isChangingTickets = !!ownedTickets;

    // Parse the ownedTickets into an array of unique ticket IDs
    let previousTicketIds: string[] = [];
    if (isChangingTickets) {
      if (Array.isArray(ownedTickets)) {
        previousTicketIds = ownedTickets;
      } else {
        previousTicketIds = ownedTickets.toString().split(",");
      }
    }

    const addonLineItems =
      addonItems?.map((addon) => ({
        quantity: addon.quantity,
        type: LineItemType.Addon,
        addonId: addon.id,
      })) || [];

    // IMPROVED TICKET ID DISTRIBUTION (#7)
    // Create a more explicit mapping between old tickets and new line items

    // First, validate ticket counts match - we've already checked this in useFooterActions
    // but this is a safeguard to ensure proper 1:1 mapping
    const totalTicketQuantity = items.reduce(
      (sum, ticket) => sum + ticket.quantity,
      0
    );
    const totalPreviousTickets = previousTicketIds.length;

    if (isChangingTickets && totalTicketQuantity !== totalPreviousTickets) {
      console.warn(
        `Ticket count mismatch: ${totalTicketQuantity} new tickets vs ${totalPreviousTickets} previous tickets`
      );
    }

    // Copy the array so we don't modify the original
    const ticketIdsToUse = [...previousTicketIds];

    // Create line items with explicit mapping to previous tickets
    const ticketLineItems = items.flatMap((ticket) => {
      const lineItems: {
        quantity: number;
        ticketTypeId?: string;
        membershipTypeId?: string;
        type: LineItemType;
        seatZone?: string;
        previousTicketId?: string;
      }[] = [];

      // For each quantity of this ticket type
      for (let i = 0; i < ticket.quantity; i++) {
        const lineItem = {
          quantity: 1, // Each line item gets quantity 1
          ...(type === "event" && {
            ticketTypeId: ticket.id,
            type: LineItemType.Ticket,
          }),
          ...(type === "membership" && {
            membershipTypeId: ticket.id,
            type: LineItemType.Membership,
          }),
          seatZone: ticket.zoneName,
        };

        // Add previousTicketId only if we have one available
        if (isChangingTickets && ticketIdsToUse.length > 0) {
          const previousTicketId = ticketIdsToUse.shift(); // Take one ID from the array
          if (previousTicketId) {
            // Just add the previousTicketId without the metadata
            // We can't include _exchangeInfo as it's not part of the LineItemInput type
            Object.assign(lineItem, {
              previousTicketId,
            });
          }
        }

        lineItems.push(lineItem);
      }

      return lineItems;
    });

    // Handle any remaining previousTicketIds if there are more owned tickets than new tickets being purchased
    // This shouldn't happen in regular flow due to quantity validation, but is here as a safeguard
    if (ticketIdsToUse?.length > 0) {
      console.warn(
        `There are ${ticketIdsToUse.length} unused previous ticket IDs that weren't assigned to new line items`
      );
    }

    // Log mapping information for debugging
    if (isChangingTickets) {
      // Store metadata in localStorage for debugging if needed
      try {
        localStorage.setItem(
          "ticketExchangeDebug",
          JSON.stringify({
            timestamp: new Date().toISOString(),
            mappings: ticketLineItems.map((item) => ({
              oldTicketId: item.previousTicketId || "none",
              newTicketType:
                type === "event" ? item.ticketTypeId : item.membershipTypeId,
              zone: item.seatZone,
            })),
          })
        );
      } catch (e) {
        // Ignore localStorage errors
      }
    }

    const input: CreateOrderInput = {
      lineItems: [...ticketLineItems, ...addonLineItems],
      multiBuyId,
      orderType,
      ...(releasePasswordId && { releasePasswordId }),
      ...(accessCodeId && { accessCodeId }),
      ...referralIdOrCode,
      ...(type === "event"
        ? {
            releaseId: releaseId,
          }
        : type === "membership"
        ? {
            membershipId: releaseId ?? membershipId,
          }
        : {
            releaseId: releaseId,
          }),
    };

    return await createOrderAndWaitForCompletion(
      input,
      errorCallback,
      onSuccess,
      options
    );
  };

  return {
    handleGAOrderCreation,
  };
}

export function usePOSIdLocalStorage() {
  const router = useRouter();

  useEffect(() => {
    if (router.query.posId) {
      if (localStorage.getItem("posId")) {
        localStorage.removeItem("posId");
        localStorage.setItem("posId", router.query.posId as string);
      } else {
        localStorage.setItem("posId", router.query.posId as string);
      }
    }
  }, [router.query.posId]);
}

export function useQueryFilteredZones(
  data: IReleaseZone[],
  query: string | string[]
) {
  const showAllZones = !query;

  const zones = data?.filter((item) => {
    if (showAllZones) return item;

    const name = normaliseString(item.name);

    if (Array.isArray(query)) {
      return query.map(normaliseString).includes(name);
    }

    return normaliseString(query) === name;
  });

  return zones;
}

function normaliseString(str: string) {
  return str.trim().toLowerCase().replace(/\s/g, "");
}
